import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { LiveProvider, LiveError, LivePreview } from "react-live";
import * as LucideIcons from "lucide-react";
import { Shield, Info, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadFontsFromData, getFontFamily } from "../../utils/fontLoader";
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

// Import Chart.js components
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';

// Import all chart types from react-chartjs-2
import { 
  Pie, 
  Bar, 
  Line, 
  Doughnut, 
  Radar, 
  PolarArea,
  Bubble,
  Scatter
} from 'react-chartjs-2';

// Register ALL Chart.js components
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

export default function TailspinProspectCollateral() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [domainMismatch, setDomainMismatch] = useState(false);

  // Check if user has already authenticated (stored in sessionStorage)
  useEffect(() => {
    const storedEmail = sessionStorage.getItem(`prospect_auth_${slug}`);
    if (storedEmail) {
      setUserEmail(storedEmail);
      setIsAuthenticated(true);
      fetchCollateral(storedEmail);
    } else {
      setLoading(false);
    }
  }, [slug]);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError(null);
      
      // Decode the JWT token to get user info
      const decoded = jwtDecode(credentialResponse.credential);
      const email = decoded.email;
      const domain = email.split('@')[1];
      
      console.log("User email:", email);
      console.log("User domain:", domain);
      
      setUserEmail(email);
      
      // Store in sessionStorage for this session
      sessionStorage.setItem(`prospect_auth_${slug}`, email);
      
      // Fetch collateral with domain verification
      await fetchCollateral(email);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Error processing Google login:', err);
      setError("Failed to process authentication. Please try again.");
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google Login Failed');
    setError("Google authentication failed. Please try again.");
    setLoading(false);
  };

  const fetchCollateral = async (email) => {
    try {
      const domain = email.split('@')[1];
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
      const response = await fetch(`${apiUrl}api/tailspin/prospect/${slug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ domain }),
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          const result = await response.json();
          setAccessDenied(true);
          setDomainMismatch(true);
          setError(result.message || 'Access denied. Your domain does not match the prospect domain.');
          setLoading(false);
          return;
        }
        throw new Error(`Failed to fetch collateral: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch collateral');
      }
      
      // Extract data and template from the response structure
      const collateralData = result.data?.collateral?.collateral?.data;
      const collateralTemplate = result.data?.collateral?.collateral?.template;
      const collateralCompliance = result.data?.collateral?.collateral?.compilance ?? result.data?.collateral?.collateral?.compliance ?? null;
      
      setData(collateralData);
      setTemplate(collateralTemplate);
      setCompliance(collateralCompliance);
      
      // Load fonts from data
      if (collateralData) {
        await loadFontsFromData(collateralData);
      }
      
      console.log("Full data:", collateralData);
      console.log("Template:", collateralTemplate);
      console.log("Compliance:", collateralCompliance);
    } catch (err) {
      console.error('Error fetching collateral:', err);
      setError(err.message || "Failed to fetch collateral.");
    } finally {
      setLoading(false);
    }
  };

  // Load fonts when data changes
  useEffect(() => {
    if (data) {
      loadFontsFromData(data);
    }
  }, [data]);

  // Extract component name from template (safe even if template is null)
  const componentName = useMemo(() => {
    if (!template) return 'DynamicComponent';
    const componentMatch = template.match(/function\s+(\w+)/);
    return componentMatch ? componentMatch[1] : 'DynamicComponent';
  }, [template]);

  // Clean template - remove imports and problematic code
  const cleanTemplate = useMemo(() => {
    if (!template) return '';
    
    try {
      let cleaned = template
        .replace(/import\s+.*?from\s+['"].*?['"];?\n?/g, '')
        .replace(/export default\s+/g, "")
        // Remove window.LucideIcons and other window references
        .replace(/const\s+LucideIcons\s*=\s*window\.LucideIcons\s*\|\|\s*\{\};?\n?/g, '')
        .replace(/const\s+\{\s*motion\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '')
        .replace(/const\s+\{\s*AnimatePresence\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '')
        .replace(/const\s+motion\s*=\s*window\.motion;?\n?/g, '')
        .replace(/const\s+\{\s*React\s*\}\s*=\s*window\s*\|\|\s*\{\};?\n?/g, '')
        .replace(/\\`/g, '`')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\u00A0/g, ' ')
        .trim();
    
      return `
${cleaned}

render(<${componentName} data={data} />);
      `.trim();
    } catch (error) {
      console.error('❌ Error processing template:', error);
      return `
        function ErrorComponent() {
          return React.createElement('div', { 
            style: { padding: '20px', color: 'red', border: '1px solid red' },
            children: 'Template processing error: ' + ${JSON.stringify(error.message)}
          });
        }
        render(<ErrorComponent />);
      `.trim();
    }
  }, [template, componentName, data]);

  // Get font family helper (safe even if data is null)
  const fontFamilyHelper = useMemo(() => {
    return data ? getFontFamily(data) : null;
  }, [data]);

  // Complete scope matching CollateralPreview - include all necessary components and libraries
  const scope = useMemo(() => {
    // Ensure React is available before creating scope
    if (!React) {
      console.error('React is not available for LiveProvider scope');
      return {};
    }

    return {
      data,
      React,
      // All Chart Components
      Pie,
      Bar,
      Line,
      Doughnut,
      Radar,
      PolarArea,
      Bubble,
      Scatter,
      ChartJS,
      // Lucide Icons namespace object
      LucideIcons,
      // Lucide as alias for LucideIcons (for template compatibility)
      Lucide: LucideIcons,
      // All Lucide Icons (spread for individual access)
      ...LucideIcons,
      // Icon aliases for common variations
      LightningBolt: LucideIcons.Zap,
      Lightning: LucideIcons.Zap,
      CheckCircle: LucideIcons.CheckCircle,
      PieChart: LucideIcons.PieChart,
      CheckCircle2: LucideIcons.CheckCircle2 || LucideIcons.CheckCircle,
      BarChart3: LucideIcons.BarChart3 || LucideIcons.BarChart,
      // A11y is commonly used shorthand for Accessibility
      A11y: LucideIcons.Accessibility || LucideIcons.Info,
      // Common incorrect icon names that LLMs generate - provide fallbacks
      Routes: LucideIcons.Route,
      Dot: LucideIcons.Circle,
      // Common icon fallbacks used in templates
      MonitorSmartphone: LucideIcons.MonitorSmartphone || LucideIcons.Monitor || LucideIcons.Smartphone,
      Quote: LucideIcons.Quote || LucideIcons.MessageSquare,
      // Font helper functions
      getFontFamily: () => fontFamilyHelper,
      defaultFontFamily: fontFamilyHelper,
      // Animation library - Framer Motion
      motion,
      AnimatePresence,
      // Explicit React Hooks for safety in evaluated code
      useState: React.useState,
      useEffect: React.useEffect,
      useContext: React.useContext,
      useRef: React.useRef,
      useMemo: React.useMemo,
      useCallback: React.useCallback,
      // Also provide createElement for dynamic icon rendering
      createElement: React.createElement,
    };
  }, [data, fontFamilyHelper]);

  // Show Google Sign-In if not authenticated
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Authentication Required</h2>
            <p className="text-gray-600">
              This collateral is protected. Please sign in with Google to verify your access.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap={false}
              theme="filled_blue"
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          </div>

          <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                Only users with the correct email domain can access this content. Your domain will be verified after sign-in.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied if domain doesn't match
  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          {userEmail && (
            <p className="text-sm text-gray-500 mb-4">
              Signed in as: <span className="font-medium">{userEmail}</span>
            </p>
          )}
          <button 
            onClick={() => {
              sessionStorage.removeItem(`prospect_auth_${slug}`);
              window.location.reload();
            }}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            Sign in with a different account
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg font-medium">Loading collateral...</p>
        </div>
      </div>
    );
  }

  if (error && !accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Collateral</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md text-center">
          <div className="text-gray-400 text-5xl mb-4">📄</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Collateral Found</h2>
          <p className="text-gray-600">The requested collateral could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <LiveProvider code={cleanTemplate} scope={scope} noInline={true}>
        <div className="w-full">
          <LivePreview />
        </div>
        <LiveError className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative m-4" />
      </LiveProvider>
    </div>
  );
}

